<template>
  <div
    class="appointment-report ar-date-lock"
    :class="{ 'ar-date-lock--released': released }"
  >
    <span
      class="ar-date-lock__icon"
      aria-hidden="true"
    >
      <feather-icon
        v-if="!released"
        icon="ClockIcon"
        size="20"
      />
      <feather-icon
        v-else
        icon="UnlockIcon"
        size="20"
        :class="{ 'ar-anim-crossfade': unlockFx }"
      />
    </span>

    <template v-if="!released">
      <div class="ar-date-lock__body">
        <p class="ar-date-lock__title">
          {{ FIGMA_COPY.dateLock }}
        </p>
        <p class="ar-date-lock__countdown ar-tabular">
          {{ countdownText }}
        </p>
        <p
          v-if="hint"
          class="ar-date-lock__hint"
        >
          {{ hint }}
        </p>
      </div>
      <div
        v-if="hasActions"
        class="ar-date-lock__actions"
      >
        <a
          v-if="meetingHref"
          class="ar-btn ar-btn--secondary ar-btn--sm"
          :href="meetingHref"
          target="_blank"
          rel="noopener noreferrer"
        >
          <feather-icon
            icon="VideoIcon"
            size="16"
            aria-hidden="true"
          />
          {{ NEW_COPY.joinMeeting }}
        </a>
        <router-link
          v-if="editTo"
          :to="editTo"
          class="ar-btn ar-btn--ghost ar-btn--sm"
        >
          {{ APP_COPY.editAppointment }}
        </router-link>
        <button
          v-else-if="hasEditListener"
          type="button"
          class="ar-btn ar-btn--ghost ar-btn--sm"
          @click="$emit('edit')"
        >
          {{ APP_COPY.editAppointment }}
        </button>
      </div>
    </template>
    <p
      v-else
      class="ar-date-lock__title ar-date-lock__released"
      :class="{ 'ar-anim-crossfade': unlockFx }"
    >
      {{ NEW_COPY.formReleased }}
    </p>

    <span
      class="ar-sr-only"
      aria-live="polite"
    >{{ announcement }}</span>
  </div>
</template>

<script>
import { APP_COPY, FIGMA_COPY, NEW_COPY } from '@/constants/appointment_report_v2'
import {
  formatRemaining,
  formatShortDate,
  formatTime,
  toTimestamp,
} from '../helpers/report_ui'

const UNLOCK_FX_MS = 450

const isHttpUrl = value => {
  if (typeof value !== 'string' || !value.trim()) return false
  try {
    const url = new URL(value.trim())
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (error) {
    return false
  }
}

/**
 * Trava por horário (Figma: "O formulário será liberado no horário marcado da sua reunião!") com contagem
 * regressiva "Libera em 2 h 14 min · às 13:00" (minutos; segundos nos últimos 10 min).
 *
 * A contagem usa o relógio do SERVIDOR: `opensAt` (= `report_opens_at`) e `serverNow` (= `server_now`, com
 * `serverNowReceivedAt` = `Date.now()` do momento em que a resposta chegou) dão a diferença para o relógio
 * local — um PC com a hora errada não libera antes nem depois. Em 0: troca para "Formulário liberado"
 * (tom de sucesso, 240ms) e emite `released` (`{ immediate }` — `true` se já estava liberado ao montar).
 *
 * Ações: "Entrar na reunião" (`meetingUrl`, http/https) e "Editar Compromisso" (`editTo` = rota, ou ouça `@edit`).
 * `hint`: linha extra abaixo da contagem (ex.: o "não houve reunião" dos tipos padrão, que já pode ser reportado).
 */
export default {
  name: 'ReportDateLockBanner',
  props: {
    opensAt: {
      type: String,
      required: true,
    },
    serverNow: {
      type: String,
      default: '',
    },
    serverNowReceivedAt: {
      type: Number,
      default: null,
    },
    meetingUrl: {
      type: String,
      default: '',
    },
    editTo: {
      type: [String, Object],
      default: null,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    hint: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      APP_COPY,
      FIGMA_COPY,
      NEW_COPY,
      mountedAt: Date.now(),
      now: Date.now(),
      released: false,
      unlockFx: false,
      announcement: '',
    }
  },
  computed: {
    opensAtMs() {
      return toTimestamp(this.opensAt)
    },
    // Relógio do servidor − relógio local (ms).
    clockOffsetMs() {
      const serverMs = toTimestamp(this.serverNow)
      if (serverMs === null) return 0
      return serverMs - (this.serverNowReceivedAt || this.mountedAt)
    },
    remainingMs() {
      if (this.opensAtMs === null) return 0
      return this.opensAtMs - (this.now + this.clockOffsetMs)
    },
    remainingSeconds() {
      return Math.max(0, Math.ceil(this.remainingMs / 1000))
    },
    opensAtLabel() {
      const time = formatTime(this.opensAt)
      const opensDay = formatShortDate(this.opensAt)
      const today = formatShortDate(this.serverNow)
      // Em outro dia, o horário sozinho engana: "às 13:00 de 02/10/26".
      return today && opensDay && opensDay !== today ? `${time} de ${opensDay}` : time
    },
    countdownText() {
      return NEW_COPY.releasesIn(formatRemaining(this.remainingSeconds), this.opensAtLabel)
    },
    meetingHref() {
      return isHttpUrl(this.meetingUrl) ? this.meetingUrl.trim() : ''
    },
    hasEditListener() {
      return !!(this.$listeners && this.$listeners.edit)
    },
    hasActions() {
      return this.showActions && !!(this.meetingHref || this.editTo || this.hasEditListener)
    },
  },
  watch: {
    opensAt() {
      this.restart()
    },
    serverNow() {
      this.restart()
    },
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.start(true)
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    clearTimeout(this.tickTimer)
    clearTimeout(this.fxTimer)
  },
  methods: {
    start(initial) {
      this.now = Date.now()
      if (this.remainingMs <= 0) {
        this.release(initial)
        return
      }
      this.schedule()
    },
    restart() {
      clearTimeout(this.tickTimer)
      this.released = false
      this.unlockFx = false
      this.mountedAt = Date.now()
      this.start(true)
    },
    // Um tique por segundo, alinhado à virada do segundo restante (a aba em segundo plano pode atrasar os
    // timers — por isso o restante é sempre recalculado a partir do relógio, nunca decrementado).
    schedule() {
      clearTimeout(this.tickTimer)
      const delay = (this.remainingMs % 1000) || 1000
      this.tickTimer = setTimeout(this.tick, Math.max(50, delay))
    },
    tick() {
      this.now = Date.now()
      if (this.remainingMs <= 0) {
        this.release(false)
        return
      }
      this.schedule()
    },
    release(immediate) {
      if (this.released) return
      clearTimeout(this.tickTimer)
      this.released = true
      if (!immediate) {
        this.unlockFx = true
        this.announcement = NEW_COPY.formReleased
        clearTimeout(this.fxTimer)
        this.fxTimer = setTimeout(() => {
          this.unlockFx = false
        }, UNLOCK_FX_MS)
      }
      this.$emit('released', { immediate: !!immediate })
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible' && !this.released) this.tick()
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
.ar-date-lock {
  min-height: 72px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-radius: var(--ar-radius-card);
  background: var(--ar-brand-tint-soft);
  border: 1px solid var(--ar-brand-border-soft);
  transition:
    background-color var(--ar-dur-entrance) var(--ar-ease-standard),
    border-color var(--ar-dur-entrance) var(--ar-ease-standard);
}

.ar-date-lock--released {
  background: var(--ar-success-tint);
  border-color: var(--ar-success-border);
}

.ar-date-lock__icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ar-radius-pill);
  background: var(--ar-brand-tint);
  color: var(--ar-brand-text);
  transition:
    background-color var(--ar-dur-entrance) var(--ar-ease-standard),
    color var(--ar-dur-entrance) var(--ar-ease-standard);
}

.ar-date-lock--released .ar-date-lock__icon {
  background: var(--ar-success-tint-strong);
  color: var(--ar-success-text);
}

.ar-date-lock__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ar-date-lock__title {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-date-lock__released {
  flex: 1 1 auto;
}

.ar-date-lock__countdown {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-2);
}

.ar-date-lock__hint {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 20px;
  font-weight: 400;
  color: var(--ar-text-2);
}

.ar-date-lock__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

@media (max-width: 767.98px) {
  .ar-date-lock {
    flex-wrap: wrap;
  }

  .ar-date-lock__body {
    flex-basis: calc(100% - 56px);
  }

  .ar-date-lock__actions {
    width: 100%;
    flex-wrap: wrap;
    padding-left: 56px;
  }
}
</style>
